import React, { useContext } from "react"
import type { ReactNode } from "react"
import omit from "lodash/omit"
import { useListItem } from "@floating-ui/react"
import cx from "classnames"
import Icon from "../icon"
import { SelectContext } from "./select"

export type OptionProps = {
  /** Option content */
  children?: ReactNode
  /** Whether this option is non-interactive */
  disabled?: boolean
  /** Whether this option is currently selected */
  selected?: boolean
  /** Whether this option can be filtered in autocomplete mode */
  filtrable?: boolean
  /** Additional CSS classes */
  className?: string
  /** Whether this option represents a "create new" action */
  create?: boolean
  /** Display label (used for typeahead and display when different from children) */
  label?: ReactNode
  /** Value submitted when this option is selected */
  value?: string | number
  /** Extra data keys forwarded from option objects in autocomplete mode */
  customKeys?: Record<string, unknown>
}

const highlightText = (
  text: ReactNode,
  searchTerm: string | undefined,
  disabled = false
) => {
  if (!searchTerm || !text) return text

  const textStr = text.toString()
  const index = textStr.toLowerCase().indexOf(searchTerm.toLowerCase())

  if (index === -1) return text

  const before = textStr.slice(0, index)
  const match = textStr.slice(index, index + searchTerm.length)
  const after = textStr.slice(index + searchTerm.length)

  return (
    <>
      <span
        className={cx({
          "text-gray-500": disabled,
          "text-gray-700": !disabled
        })}
      >
        {before}
      </span>
      <span
        className={cx({
          "text-gray-500": disabled,
          "font-bold text-gray-900": !disabled
        })}
      >
        {match}
      </span>
      <span
        className={cx({
          "text-gray-500": disabled,
          "text-gray-700": !disabled
        })}
      >
        {after}
      </span>
    </>
  )
}

export default function Option({
  children,
  label,
  disabled,
  create,
  ...props
}: OptionProps) {
  const { activeIndex, selectedIndex, getItemProps, handleSelect, inputValue } =
    useContext(SelectContext)

  const content = label || children
  const { ref, index } = useListItem({
    label: disabled ? null : (content as string | null)
  })

  const isActive = activeIndex === index
  const isSelected = index >= 0 ? selectedIndex === index : false
  const allowedProps = omit(props, ["customKeys"])

  return (
    <button
      ref={ref}
      {...allowedProps}
      role="option"
      aria-selected={isSelected}
      aria-disabled={disabled}
      tabIndex={isActive && !disabled ? 0 : -1}
      data-index={index}
      className={cx(
        "flex items-center gap-1 justify-between w-full p-2 text-left text-sm focus:outline-none",
        props.className,
        {
          "relative border-t border-gray-200 pt-4 px-4 mt-2": create,
          rounded: !create,
          "text-gray-900": !disabled,
          "bg-gray-50": isActive && !disabled && !create,
          "text-gray-500 cursor-default": disabled
        }
      )}
      style={create ? { left: -8, width: "calc(100% + 16px)" } : {}}
      {...getItemProps({
        onClick: () => {
          if (disabled) return
          handleSelect(index)
        }
      })}
    >
      <span className="flex-1">
        {create ? children : highlightText(children, inputValue, disabled)}
      </span>
      {isSelected && <Icon name="check" aria-hidden="true" />}
    </button>
  )
}
